import * as THREE from 'three';
import { InkPipeline } from '../render/InkPipeline';
import { offsetDir, Planet, PLANET_RADIUS, type PlanetPalette } from '../world/Planet';
import { Character, preloadCharacters } from '../world/Character';
import { loadModels, model, type ModelKey } from '../world/Assets';
import { buildMarket, faceToward, local, MARKET, marketPads, marketPaving, NPCS, PICKUPS, type MarketBuild, type NpcSpec, type PickupSpec, type ZoneDef } from '../world/Market';
import { buildRealm, PLACES, REALM_NPCS, REALM_PICKUPS, realmHooks, realmPads, realmPaving, type RealmBuild } from '../world/Realm';
import { Studio } from '../studio/Studio';
import { PlayerController, type MoveInput } from './PlayerController';
import { FollowCamera } from './FollowCamera';
import { Audio, type AmbienceKind } from './Audio';
import { Crowd, type Walker } from './Crowd';
import { ITEMS, SHOPS, Wallet, type ItemId } from './Economy';
import { QuestLog, type QuestCtx } from './Quests';
import { Ui, type DialogueLine } from '../ui/Ui';
import { Hud, Thumbnails } from '../ui/Hud';

const PALETTE: PlanetPalette = {
  grass: 0x8fc25a,
  grassDark: 0x79b04e,
  dirt: 0xc89a62,
  rock: 0xa59a8e,
  sand: 0xe9d7a8,
  water: 0x4fa9cf,
  trunk: 0x7a4a2e,
  leaves: [0x3f8a4a, 0x5fa64c, 0x7cb84a, 0xd98a3a],
  roof: [0xc8452f],
  wall: 0xf1dfc0,
  stone: 0xb3a89c,
  cobble: 0xcdc2ae,
  cobbleDark: 0xa99d89,
};

const STEP = 1000 / 60;
const CROWD_SIZE = 18;

const FORTUNES = [
  'The well says: buy the shield.',
  'The well says: the hills are not hills.',
  'The well says: you will find coins. You just lost one.',
  'The well says: Tobin’s buns are worth it.',
  'The well says: open the Gate.',
];

type Npc = { spec: NpcSpec; char: Character; pos: THREE.Vector3; up: THREE.Vector3; rest: THREE.Quaternion; bubble: string; bubbleUntil: number; nextShout: number };
type Pickup = { spec: PickupSpec; obj: THREE.Object3D; base: THREE.Vector3; up: THREE.Vector3; taken: boolean };
type Interaction = { label: string; pos: THREE.Vector3; dist: number; act: () => void };
type State = 'loading' | 'title' | 'play' | 'dive' | 'studio';

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(50, 1, 0.1, 260);
  private readonly ink: InkPipeline;
  private readonly sun = new THREE.DirectionalLight(0xffffff, 2.4);
  private readonly ui = new Ui('BURNWOOD');
  private readonly audio = new Audio();
  private readonly wallet = new Wallet(5);
  private readonly ctx: QuestCtx = { wallet: this.wallet, flags: new Set(), counters: new Map() };
  private readonly quests = new QuestLog(this.ctx);
  private hud!: Hud;
  private planet!: Planet;
  private market!: MarketBuild;
  private realm!: RealmBuild;
  private studio!: Studio;
  private diveT = -1;
  private fromSkip = false;
  private zones: ZoneDef[] = [];
  private player!: PlayerController;
  private hero!: Character;
  private follow!: FollowCamera;
  private crowd!: Crowd;
  private thief!: Walker;
  private readonly npcs: Npc[] = [];
  private readonly pickups: Pickup[] = [];
  private readonly dummyHits = new Set<number>();
  private readonly dummyWobble: number[] = [];
  private state: State = 'loading';
  private frame = 0;
  private last = 0;
  private acc = 0;
  private time = 0;
  private readonly heroPos = new THREE.Vector3();
  private readonly heroQuat = new THREE.Quaternion();
  private readonly keys = new Set<string>();
  private readonly mouse = { steering: false, x: 0, y: 0, orbiting: false, lx: 0, ly: 0 };
  private readonly stick = { id: -1, cx: 0, cy: 0, x: 0, y: 0 };
  private readonly look = { id: -1, x: 0, y: 0, moved: 0 };
  private dialogue: { lines: DialogueLine[]; i: number; npc: Npc | null; onClose: () => void } | null = null;
  private interaction: Interaction | null = null;
  private zone: string | null = null;
  private flyIn = 0;
  private titleSpin = 0;
  private hintTimer = 0;
  private cullTick = 0;
  private gearKey = '';
  private readonly walkerBubbles = new Map<Walker, { text: string; until: number }>();

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, stencil: false, powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.autoClear = false;
    this.ink = new InkPipeline(this.renderer, {
      ink: 0x231c1a,
      skyTop: 0x4fa8e3,
      skyBottom: 0xd9f0f6,
      fog: 0xcfe7ef,
      fogRange: [40, 130],
      lineFade: [12, 95],
      lineThickness: 1.5,
      saturation: 1.12,
      tint: 0xfffaf2,
    });

    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    Object.assign(this.sun.shadow.camera, { left: -20, right: 20, top: 20, bottom: -20, near: 1, far: 90 });
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.05;
    this.scene.add(this.sun, this.sun.target);

    this.ui.onLetter = (i) => this.audio.ready && this.audio.blip(1 + (i % 4) * 0.12);
    this.ui.onType = () => this.audio.blip(0.9);
    this.ui.beginButton.addEventListener('click', () => void this.begin());
    this.ui.muteButton.addEventListener('click', () => {
      this.audio.setMuted(!this.audio.isMuted);
      this.ui.setMuted(this.audio.isMuted);
    });
    document.querySelector('#dialogue-next')!.addEventListener('click', () => this.advanceDialogue());
    document.querySelector('#bag-btn')!.addEventListener('click', () => this.state === 'play' && this.togglePanel('bag'));
    document.querySelector('#log-btn')!.addEventListener('click', () => this.state === 'play' && this.togglePanel('log'));
    this.bindInput();
  }

  async start(): Promise<void> {
    const hint = document.querySelector('#load-hint')!;
    await loadModels(undefined, (f) => (hint.textContent = `Loading the market… ${Math.round(f * 70)}%`));
    await preloadCharacters(['Rogue', 'Barbarian', 'Knight', 'Mage']);
    hint.textContent = 'Setting up stalls…';
    await this.buildWorld();
    hint.textContent = 'Sound on. It’s loud in there.';
    this.state = 'title';
    this.ui.beginButton.disabled = false;
    this.last = performance.now();
    this.frame = requestAnimationFrame(this.tick);
    // ?studio boots straight into Burnwood Studios.
    if (new URLSearchParams(location.search).has('studio')) void this.skipToStudio();
    (window as unknown as { __burnwood: unknown }).__burnwood = {
      begin: () => this.begin(),
      state: () => this.state,
      player: () => this.player.position.toArray(),
      local: () => this.planet.toLocal(MARKET, this.player.position).toArray(),
      teleport: (x: number, z: number) => this.teleport(x, z),
      teleportDir: (x: number, y: number, z: number) => {
        const p = this.planet.surfacePoint(new THREE.Vector3(x, y, z), 0.5);
        this.player.position.copy(p);
        this.player.prevPosition.copy(p);
        this.player.velocity.set(0, 0, 0);
      },
      places: () => Object.fromEntries(Object.entries(PLACES).map(([k, v]) => [k, v.toArray()])),
      enterDungeon: () => this.enterDungeon(),
      studioGo: (p: number) => ((this.studio as unknown as { targetP: number }).targetP = p),
      studioP: () => (this.studio as unknown as { p: number }).p,
      face: (x: number, z: number) => {
        const f = local(this.planet, x, z).sub(this.player.position).projectOnPlane(this.player.up).normalize();
        this.player.facing.copy(f);
        this.player.prevFacing.copy(f);
        this.follow.snapBehind(f);
      },
      interact: () => this.interaction?.act(),
      interaction: () => this.interaction?.label ?? null,
      advance: () => this.advanceDialogue(),
      dialogueOpen: () => !!this.dialogue,
      coins: () => this.wallet.coins,
      wallet: this.wallet,
      quests: this.quests,
    };
  }

  private async buildWorld(): Promise<void> {
    const helper = { offset: offsetDir };
    this.planet = new Planet(PALETTE, [...marketPads(helper), ...realmPads()], 7, [...marketPaving(helper), ...realmPaving()], realmHooks);
    this.scene.add(this.planet.group);
    this.market = buildMarket(this.planet);
    this.realm = buildRealm(this.planet);
    this.zones = [...this.market.zones, ...this.realm.zones].sort((a, b) => a.radius - b.radius);

    for (const spec of [...NPCS, ...REALM_NPCS]) {
      const char = await Character.create(spec.kind, spec.opts);
      char.root.position.copy(spec.dir ? this.planet.surfacePoint(spec.dir) : local(this.planet, spec.at[0], spec.at[1]));
      faceToward(char.root, spec.dir ? this.planet.surfacePoint(MARKET) : local(this.planet, ...(spec.face ?? [0, 0])));
      this.scene.add(char.root);
      this.planet.addCollider(new THREE.CylinderGeometry(0.4, 0.4, 1.4, 8).translate(0, 0.7, 0), char.root);
      this.npcs.push({ spec, char, pos: char.root.position.clone(), up: char.root.position.clone().normalize(), rest: char.root.quaternion.clone(), bubble: '', bubbleUntil: 0, nextShout: 3 + Math.random() * 8 });
    }

    for (const spec of [...PICKUPS, ...REALM_PICKUPS]) {
      const key: ModelKey = spec.item === 'coins' ? 'coinsSmall' : spec.item === 'purse' ? 'coinsLarge' : ITEMS[spec.item].model;
      const obj = model(key, { height: spec.item === 'ingots' ? 0.7 : 0.45 });
      const base = spec.dir ? this.planet.surfacePoint(spec.dir, 0.15) : local(this.planet, spec.at[0], spec.at[1], 0.15);
      obj.position.copy(base);
      faceToward(obj, local(this.planet, 0, 0));
      obj.visible = !spec.quest;
      this.scene.add(obj);
      this.pickups.push({ spec, obj, base, up: base.clone().normalize(), taken: false });
    }

    const collider = this.planet.buildCollider();
    const spawn = local(this.planet, 1.2, -9.5, 0.4);
    this.player = new PlayerController(collider, spawn, { jump: () => this.audio.jump(), land: () => this.audio.land() });
    const toCentre = local(this.planet, 0, 0).sub(spawn).projectOnPlane(this.player.up).normalize();
    this.player.facing.copy(toCentre);
    this.player.prevFacing.copy(toCentre);

    this.hero = await Character.create('Rogue', { hideHeadgear: false });
    this.scene.add(this.hero.root);
    this.follow = new FollowCamera(this.camera);
    this.follow.snapBehind(this.player.facing);

    this.crowd = new Crowd(this.planet, this.market.obstacles, this.scene);
    await this.crowd.spawn(CROWD_SIZE);
    this.thief = await this.crowd.spawnThief();

    this.studio = new Studio(this.renderer, { onExit: () => this.leaveStudio(), roar: () => this.audio.roar(), whoosh: () => this.audio.whoosh() });
    document.querySelector('#skip-studio')!.addEventListener('click', () => void this.skipToStudio());

    const thumbs = new Thumbnails(this.renderer);
    this.hud = new Hud(this.wallet, this.quests, thumbs);
    this.hud.onBuy = (id) => this.buy(id);
    this.hud.onEquip = (id) => this.wallet.equip(id);
    this.hud.onClosePanel = () => (this.player.frozen = false);
    this.wallet.onEvent = (e) => {
      if (e.type === 'coins' && e.amount) this.hud.toast(`${e.amount > 0 ? '+' : ''}${e.amount} coins`, '', 'coin');
      if (e.type === 'gain' && e.item && !ITEMS[e.item].stacks) this.hud.toast(`Got: ${ITEMS[e.item].name}`, '', 'item');
    };
    this.wallet.subscribe(() => this.syncGear());
    this.quests.onEvent = (e) => {
      if (e.type === 'started') {
        this.hud.toast(e.quest.kind === 'main' ? 'Main quest' : 'Side quest', e.quest.title, 'quest');
        this.audio.questStart();
        // The Herald fronts you some coin so a blade and a shield are within reach.
      } else if (e.type === 'completed') {
        this.hud.toast('Quest complete!', `${e.quest.title}  ·  +${e.quest.reward.coins} coins`, 'quest');
        this.audio.questDone();
      } else if (e.step) {
        this.hud.toast('Next', e.step.text, 'info');
        this.audio.pop();
      }
      this.hud.refresh();
    };
    this.hud.refresh();
  }

  private async begin(): Promise<void> {
    if (this.state !== 'title') return;
    this.audio.start();
    this.audio.whoosh();
    this.ui.setMuted(false);
    await this.ui.wipeThrough(() => {
      this.ui.hideTitle();
      this.state = 'play';
      this.flyIn = 1;
      document.querySelector('#hud')!.classList.add('on');
    });
    document.querySelector('#controls-hint')!.classList.add('on');
    this.hintTimer = window.setTimeout(() => document.querySelector('#controls-hint')!.classList.remove('on'), 10000);
  }

  private teleport(x: number, z: number): void {
    const p = local(this.planet, x, z, 0.5);
    this.player.position.copy(p);
    this.player.prevPosition.copy(p);
    this.player.velocity.set(0, 0, 0);
  }

  // ---------------- economy + gear ----------------

  private buy(id: ItemId): void {
    const r = this.wallet.buy(id);
    if (r === 'ok') {
      this.audio.coin();
      this.hud.toast(`Bought ${ITEMS[id].name}`, `-${ITEMS[id].price} coins`, 'item');
    } else {
      this.audio.deny();
      if (r === 'poor') this.hud.toast('Not enough coins', 'Side quests pay. So do loose coins.', 'info');
    }
  }

  /** Show equipped weapon/shield (and carried crates) on the hero. */
  private syncGear(): void {
    const eq = this.wallet.equipped;
    const carrying = this.wallet.has('ingots');
    const key = `${eq.weapon}|${eq.shield}|${carrying}`;
    if (key === this.gearKey) return;
    this.gearKey = key;
    const raw = (k: ModelKey) => model(k, { ground: false });
    this.hero.hold('weapon', eq.weapon && !carrying ? raw(ITEMS[eq.weapon].model) : null);
    this.hero.hold('shield', eq.shield && !carrying ? raw(ITEMS[eq.shield].model) : null);
    this.hero.hold('carry', carrying ? model('box', { height: 1.4 }) : null);
    this.player.speedScale = carrying ? 0.6 : 1;
  }

  // ---------------- input ----------------

  private togglePanel(which: 'bag' | 'log'): void {
    if (this.dialogue) return;
    if (which === 'bag') this.hud.toggleBag();
    else this.hud.toggleLog();
    this.player.frozen = this.hud.panelOpen;
  }

  private bindInput(): void {
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
      if (this.state !== 'play') return;
      if (e.code === 'Escape') {
        if (this.dialogue) this.closeDialogue();
        else this.hud.closePanels();
        return;
      }
      if (e.code === 'KeyI') return this.togglePanel('bag');
      if (e.code === 'KeyJ') return this.togglePanel('log');
      if (this.hud?.panelOpen) return;
      if (e.code === 'KeyE' || e.code === 'Enter' || (e.code === 'Space' && this.dialogue)) {
        if (this.dialogue) this.advanceDialogue();
        else this.interaction?.act();
      } else if (e.code === 'Space') {
        this.player.requestJump(performance.now());
      }
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());

    const c = this.canvas;
    c.addEventListener('contextmenu', (e) => e.preventDefault());
    c.addEventListener('pointerdown', (e) => {
      if (this.state !== 'play' || this.hud.panelOpen) return;
      c.setPointerCapture(e.pointerId);
      if (e.pointerType === 'touch') {
        if (e.clientX < innerWidth * 0.45 && this.stick.id < 0) {
          Object.assign(this.stick, { id: e.pointerId, cx: e.clientX, cy: e.clientY, x: 0, y: 0 });
          const el = document.querySelector<HTMLElement>('#stick')!;
          el.style.left = `${e.clientX}px`;
          el.style.top = `${e.clientY}px`;
          el.classList.add('on');
        } else if (this.look.id < 0) {
          Object.assign(this.look, { id: e.pointerId, x: e.clientX, y: e.clientY, moved: 0 });
          this.follow.steering = true;
        }
        return;
      }
      if (this.dialogue) return this.advanceDialogue();
      if (e.button === 0) Object.assign(this.mouse, { steering: true, x: e.clientX, y: e.clientY });
      if (e.button === 2 || e.button === 1) {
        Object.assign(this.mouse, { orbiting: true, lx: e.clientX, ly: e.clientY });
        this.follow.steering = true;
      }
    });
    c.addEventListener('pointermove', (e) => {
      if (e.pointerId === this.stick.id) {
        const dx = e.clientX - this.stick.cx, dy = e.clientY - this.stick.cy;
        const len = Math.min(1, Math.hypot(dx, dy) / 50);
        const a = Math.atan2(dy, dx);
        this.stick.x = Math.cos(a) * len;
        this.stick.y = -Math.sin(a) * len;
        document.querySelector<HTMLElement>('#stick-knob')!.style.transform = `translate(${this.stick.x * 34}px, ${-this.stick.y * 34}px)`;
      } else if (e.pointerId === this.look.id) {
        this.follow.orbit(e.clientX - this.look.x, e.clientY - this.look.y);
        this.look.moved += Math.abs(e.clientX - this.look.x) + Math.abs(e.clientY - this.look.y);
        this.look.x = e.clientX;
        this.look.y = e.clientY;
      } else if (this.mouse.orbiting) {
        this.follow.orbit(e.clientX - this.mouse.lx, e.clientY - this.mouse.ly);
        this.mouse.lx = e.clientX;
        this.mouse.ly = e.clientY;
      }
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
    });
    const end = (e: PointerEvent) => {
      if (e.pointerId === this.stick.id) {
        this.stick.id = -1;
        this.stick.x = this.stick.y = 0;
        document.querySelector('#stick')!.classList.remove('on');
        document.querySelector<HTMLElement>('#stick-knob')!.style.transform = '';
      } else if (e.pointerId === this.look.id) {
        this.look.id = -1;
        this.follow.steering = false;
        // A tap (no drag) interacts.
        if (this.look.moved < 12) {
          if (this.dialogue) this.advanceDialogue();
          else this.interaction?.act();
        }
      }
      if (e.button === 0) this.mouse.steering = false;
      if (e.button === 2 || e.button === 1) {
        this.mouse.orbiting = false;
        this.follow.steering = false;
      }
    };
    c.addEventListener('pointerup', end);
    c.addEventListener('pointercancel', end);
    c.addEventListener('wheel', (e) => this.follow?.zoom(e.deltaY), { passive: true });
    document.querySelector('#talk-prompt')!.addEventListener('click', () => this.interaction?.act());

  }

  private readInput(): MoveInput {
    let x = 0, y = 0;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) y += 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) y -= 1;
    x += this.stick.x;
    y += this.stick.y;
    if (this.mouse.steering) {
      const p = this.heroPos.clone().addScaledVector(this.player.up, 0.65).project(this.camera);
      const sx = (p.x * 0.5 + 0.5) * innerWidth, sy = (-p.y * 0.5 + 0.5) * innerHeight;
      x += THREE.MathUtils.clamp((this.mouse.x - sx) / 140, -1, 1);
      y += THREE.MathUtils.clamp((sy - this.mouse.y) / 140, -1, 1);
    }
    const len = Math.hypot(x, y);
    if (len > 1) { x /= len; y /= len; }
    const sprint = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') || Math.hypot(this.stick.x, this.stick.y) > 0.95;
    return { x, y, sprint, jump: false };
  }

  // ---------------- dialogue ----------------

  private say(lines: DialogueLine[], npc: Npc | null, onClose: () => void = () => {}): void {
    if (!lines.length) return onClose();
    this.dialogue = { lines, i: 0, npc, onClose };
    this.player.frozen = true;
    this.ui.hideZone();
    document.querySelector('#controls-hint')!.classList.remove('on');
    this.ui.showLine(lines[0]);
    if (npc) {
      const up = npc.up;
      const mid = npc.pos.clone().lerp(this.player.position, 0.5).addScaledVector(up, 1.0);
      const across = npc.pos.clone().sub(this.player.position).projectOnPlane(up).normalize();
      const side = new THREE.Vector3().crossVectors(up, across).normalize();
      if (side.dot(this.follow.back) < 0) side.negate();
      this.follow.setShot({ target: mid, back: side.add(this.follow.back.clone().multiplyScalar(0.6)).normalize(), distance: 4, pitch: 0.14 });
    }
  }

  private advanceDialogue(): void {
    const d = this.dialogue;
    if (!d) return;
    if (!this.ui.typingDone) return this.ui.finishTyping();
    d.i += 1;
    if (d.i >= d.lines.length) return this.closeDialogue();
    this.ui.showLine(d.lines[d.i]);
  }

  private closeDialogue(): void {
    const d = this.dialogue;
    this.dialogue = null;
    this.player.frozen = false;
    this.ui.closeDialogue();
    this.follow.setShot(null);
    d?.onClose();
  }

  private talkTo(npc: Npc): void {
    const conv = this.quests.conversation(npc.spec.id);
    if (conv) return this.say(conv.lines, npc, conv.onClose);
    if (npc.spec.shop) {
      this.player.frozen = true;
      this.hud.openShop(npc.spec.shop, npc.spec.name, npc.spec.color, npc.spec.chatter[0]);
      return;
    }
    const line = npc.spec.chatter[Math.floor(Math.random() * npc.spec.chatter.length)];
    this.say([{ speaker: npc.spec.name, color: npc.spec.color, text: line }], npc);
  }

  // ---------------- interactions ----------------

  private findInteraction(): Interaction | null {
    if (this.state !== 'play' || this.dialogue || this.hud.panelOpen) return null;
    const p = this.player.position;
    let best: Interaction | null = null;
    // Named characters win over passers-by: bias the distance by importance.
    const offer = (label: string, pos: THREE.Vector3, range: number, act: () => void, bias = 0) => {
      const raw = pos.distanceTo(p);
      const dist = raw + bias;
      if (raw < range && (!best || dist < best.dist)) best = { label, pos, dist, act };
    };
    for (const npc of this.npcs) {
      const label = npc.spec.shop && !this.quests.conversation(npc.spec.id) ? 'Shop' : 'Talk';
      offer(label, npc.pos, 2.7, () => this.talkTo(npc), -1.2);
    }
    const thiefActive = this.thiefActive;
    for (const w of this.crowd.walkers) {
      if (w.thief) {
        if (thiefActive) offer('Grab!', w.char.root.position, 1.7, () => this.catchThief(), -2);
        continue;
      }
      offer('Chat', w.char.root.position, 1.6, () => this.chat(w), 0.8);
    }
    for (const pk of this.pickups) {
      if (pk.taken || !pk.obj.visible) continue;
      offer('Pick up', pk.base, 1.8, () => this.take(pk));
    }
    this.market.dummies.forEach((d, i) => offer('Strike', d.position, 2.3, () => this.strike(i)));
    offer('Toss a coin', this.market.wellPos, 2.6, () => this.tossCoin());
    offer('Enter the dungeon', this.realm.dungeonDoor, 3.2, () => this.enterDungeon(), -2);
    return best;
  }

  private get thiefActive(): boolean {
    return this.quests.get('thief').status === 'active' && !this.ctx.flags.has('caughtThief');
  }

  private chat(w: Walker): void {
    this.crowd.hold(w, 3.5, this.player.position);
    this.walkerBubbles.set(w, { text: w.line, until: this.time + 3.2 });
    this.audio.blip(1.1);
  }

  private catchThief(): void {
    this.ctx.flags.add('caughtThief');
    this.walkerBubbles.delete(this.thief);
    this.hud.toast('Caught him!', 'He drops Fennick’s pouch and bolts.', 'item');
    this.audio.hit();
  }

  private take(pk: Pickup): void {
    pk.taken = true;
    pk.obj.visible = false;
    this.audio.pickup();
    if (pk.spec.item === 'coins') this.wallet.earn(5);
    else this.wallet.give(pk.spec.item);
  }

  private strike(i: number): void {
    if (!this.wallet.equipped.weapon) {
      this.audio.deny();
      this.hud.toast('No weapon in hand', 'Buy one at the Forge, then equip it (I).', 'info');
      return;
    }
    this.hero.playOnce('1H_Melee_Attack_Chop', 0.1);
    this.player.facing.copy(this.market.dummies[i].position.clone().sub(this.player.position).projectOnPlane(this.player.up).normalize());
    setTimeout(() => {
      this.dummyWobble[i] = 1;
      this.audio.hit();
      if (this.quests.get('range').status === 'active' && !this.dummyHits.has(i)) {
        this.dummyHits.add(i);
        this.ctx.counters.set('dummies', this.dummyHits.size);
        this.hud.toast(`Dummy ${this.dummyHits.size}/3`, '', 'info');
        this.hud.refresh();
      }
    }, 280);
  }

  private tossCoin(): void {
    if (this.wallet.coins < 1) {
      this.hud.toast('Your pockets are empty', '', 'info');
      return;
    }
    this.wallet.earn(-1);
    this.audio.coin();
    this.hud.toast(FORTUNES[Math.floor(Math.random() * FORTUNES.length)], '', 'info');
  }

  // ---------------- loop ----------------

  private readonly tick = (now: number) => {
    this.frame = requestAnimationFrame(this.tick);
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    this.time += dt;
    this.resize();
    if (this.state === 'studio') {
      this.studio.update(dt, this.time);
      this.studio.render();
      return;
    }

    if (this.state === 'play') {
      this.acc += dt * 1000;
      const input = this.readInput();
      const fwd = this.follow.forward(new THREE.Vector3());
      while (this.acc >= STEP) {
        this.player.tick(input, fwd, now);
        this.acc -= STEP;
      }
    }
    this.player.sample(this.acc / STEP, this.heroPos, this.heroQuat);
    this.hero.root.position.copy(this.heroPos);
    this.hero.root.quaternion.copy(this.heroQuat);
    const speed = this.player.speed;
    this.hero.update(dt, this.time, { speed: this.player.frozen ? 0 : speed, air: this.player.inAir, talking: false });

    if (this.state === 'play') this.updateJourney();
    if (this.state === 'dive') this.updateDive(dt);
    const p2 = this.planet.toLocal(MARKET, this.player.position);
    const thiefActive = this.thiefActive;
    // The bazaar crowd only lives when you're near the bazaar.
    const inTown = this.near(this.planet.surfacePoint(MARKET), 40);
    for (const w of this.crowd.walkers) w.char.root.visible = inTown && (!w.thief || thiefActive);
    if (inTown) this.crowd.update(dt, this.time, p2, this.player.frozen, thiefActive);
    this.updateNpcs(dt);
    this.updateWorld(dt);
    this.updateCamera(dt, speed);
    this.updateZones();
    this.quests.update();
    this.interaction = this.findInteraction();
    this.updateMarkers();
    if (!this.player.inAir && !this.player.frozen) this.audio.footstep(speed);

    // No fog on the far-away title camera; normal fog once you're on the ground.
    const fog = this.ink.uniforms.uFog.value as THREE.Vector2;
    if (this.state === 'play' || this.state === 'dive') fog.set(40 + this.flyIn * 60, 130 + this.flyIn * 120);
    else fog.set(160, 360);
    this.ink.render(this.scene, this.camera);
  };

  /** Quest flags for the trip to Mount Cinder. */
  private updateJourney(): void {
    const pdir = this.player.position.clone().normalize();
    if (pdir.angleTo(PLACES.peak) * PLANET_RADIUS < 32) this.ctx.flags.add('atMountain');
  }

  private enterDungeon(): void {
    const w = this.wallet.equipped.weapon;
    if (w !== 'sword' && w !== 'greatsword') {
      this.audio.deny();
      this.hud.toast('The door won\u2019t budge', 'Something in there wants you holding a sword.', 'info');
      return;
    }
    this.ctx.flags.add('atMountain');
    this.ctx.flags.add('enteredDungeon');
    this.quests.update();
    this.state = 'dive';
    this.diveT = 0;
    this.player.frozen = true;
    this.ui.hideZone();
    this.ui.setPrompt(0, 0, false);
    // Face the doorway so the dive goes in, not sideways.
    const into = this.realm.dungeonDoor.clone().sub(this.player.position).projectOnPlane(this.player.up).normalize();
    this.player.facing.copy(into);
    this.player.prevFacing.copy(into);
    this.follow.snapBehind(into);
    this.audio.roar();
  }

  /** Push the camera into the hero's eyes, fade to black, then hand over to the studio. */
  private updateDive(dt: number): void {
    this.diveT += dt;
    const t = Math.min(1, this.diveT / 1.4);
    const e = t * t * (3 - 2 * t);
    this.follow.distance = 7 * (1 - e) + 0.15;
    this.follow.pitch = 0.42 * (1 - e);
    if (this.diveT > 0.7) document.querySelector('#fade')!.classList.add('on');
    if (this.diveT > 1.7 && this.state === 'dive') {
      this.state = 'studio';
      this.setHudVisible(false);
      this.audio.setAmbience({});
      this.audio.setMusic(false);
      void this.studio.enter().then(() => setTimeout(() => document.querySelector('#fade')!.classList.remove('on'), 150));
    }
  }

  private setHudVisible(on: boolean): void {
    for (const sel of ['#hud', '#side', '#markers', '#toasts', '#zone-title']) document.querySelector<HTMLElement>(sel)!.style.visibility = on ? '' : 'hidden';
  }

  private leaveStudio(): void {
    document.querySelector('#fade')!.classList.add('on');
    setTimeout(() => {
      this.studio.exit();
      this.audio.setMusic(true);
      this.setHudVisible(true);
      if (this.fromSkip) {
        this.fromSkip = false;
        this.ui.hideTitle();
        this.flyIn = 1;
      } else {
        // Step back out of the doorway, onto the mountain shelf.
        const out = this.realm.dungeonDoor.clone().normalize();
        const back = out.clone().applyAxisAngle(new THREE.Vector3().crossVectors(out, MARKET).normalize(), 5 / PLANET_RADIUS);
        const p = this.planet.surfacePoint(back, 0.5);
        this.player.position.copy(p);
        this.player.prevPosition.copy(p);
        this.player.velocity.set(0, 0, 0);
        const f = this.planet.surfacePoint(MARKET).sub(p).projectOnPlane(p.clone().normalize()).normalize();
        this.player.facing.copy(f);
        this.player.prevFacing.copy(f);
        this.follow.snapBehind(f);
      }
      this.follow.distance = 7;
      this.follow.pitch = 0.42;
      this.player.frozen = false;
      this.state = 'play';
      document.querySelector('#fade')!.classList.remove('on');
    }, 900);
  }

  /** Title-screen skip link: straight into the studio. */
  private async skipToStudio(): Promise<void> {
    if (this.state !== 'title') return;
    this.audio.start();
    this.fromSkip = true;
    document.querySelector('#fade')!.classList.add('on');
    await new Promise((r) => setTimeout(r, 700));
    this.ui.hideTitle();
    this.setHudVisible(false);
    document.querySelector('#hud')!.classList.add('on');
    this.state = 'studio';
    this.audio.setMusic(false);
    await this.studio.enter();
    document.querySelector('#fade')!.classList.remove('on');
  }

  private updateNpcs(dt: number): void {
    for (const npc of this.npcs) {
      const dist = npc.pos.distanceTo(this.player.position);
      const talkingNow = this.dialogue?.npc === npc;
      if (dist < 5 || talkingNow) {
        const to = this.player.position.clone().sub(npc.pos).projectOnPlane(npc.up).normalize();
        const x = new THREE.Vector3().crossVectors(npc.up, to).normalize();
        const target = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, npc.up, to));
        npc.char.root.quaternion.slerp(target, 1 - Math.exp(-dt * 3));
      } else {
        npc.char.root.quaternion.slerp(npc.rest, 1 - Math.exp(-dt * 1.5));
      }
      const seen = this.near(npc.pos);
      npc.char.root.visible = seen;
      if (seen) npc.char.update(dt, this.time, { speed: 0, air: false, talking: talkingNow && !this.ui.typingDone });
      // Vendors and the Herald shout over the crowd.
      if (npc.spec.shouts && this.time > npc.nextShout) {
        npc.nextShout = this.time + 6 + Math.random() * 8;
        if (dist < 22 && !talkingNow) {
          npc.bubble = npc.spec.shouts[Math.floor(Math.random() * npc.spec.shouts.length)];
          npc.bubbleUntil = this.time + 3;
          if (npc.spec.id === 'herald') npc.char.playOnce('Cheer', 0.2);
        }
      }
    }
  }

  /** Hide props and people on the far side of the world (the planet hides them anyway). */
  private cullFarSide(): void {
    if (++this.cullTick % 8) return;
    const onGround = this.state === 'play' || this.state === 'dive';
    const focus = onGround ? this.player.position.clone().normalize() : null;
    const kids = this.planet.group.children;
    for (let i = 2; i < kids.length; i++) {
      const o = kids[i];
      o.visible = !focus || o.position.lengthSq() < 1 || o.position.clone().normalize().dot(focus) > 0.2;
    }
  }

  /** People far away neither render nor animate. */
  private near(p: THREE.Vector3, range = 46): boolean {
    return this.state !== 'play' && this.state !== 'dive' ? true : p.distanceTo(this.player.position) < range;
  }

  private updateWorld(dt: number): void {
    this.cullFarSide();
    this.market.dummies.forEach((d, i) => {
      const w = this.dummyWobble[i] ?? 0;
      if (w <= 0) return;
      this.dummyWobble[i] = Math.max(0, w - dt * 1.4);
      (d.userData.wobble as THREE.Object3D).rotation.x = Math.sin((1 - w) * 22) * 0.35 * w;
    });
    for (const pk of this.pickups) {
      if (pk.taken) continue;
      if (pk.spec.quest) pk.obj.visible = this.quests.get(pk.spec.quest).status === 'active';
      pk.obj.position.copy(pk.base).addScaledVector(pk.up, Math.sin(this.time * 3 + pk.base.x) * 0.08);
      pk.obj.rotateY(dt * 1.2);
    }
    const onGround = this.state === 'play' || this.state === 'dive';
    const up = onGround ? this.player.up : new THREE.Vector3(0, 1, 0);
    const side = new THREE.Vector3().crossVectors(up, this.follow.back).normalize();
    const sunDir = up.clone().multiplyScalar(1.15).addScaledVector(side, 0.6).addScaledVector(this.follow.back, 0.3).normalize();
    const s = this.sun.shadow.camera;
    if (onGround) {
      this.sun.position.copy(this.heroPos).addScaledVector(sunDir, 40);
      this.sun.target.position.copy(this.heroPos);
      s.left = s.bottom = -20;
      s.right = s.top = 20;
    } else {
      this.sun.position.copy(this.camera.position).normalize().multiplyScalar(70).add(new THREE.Vector3(25, 35, 0));
      this.sun.target.position.set(0, 0, 0);
      s.left = s.bottom = -PLANET_RADIUS - 8;
      s.right = s.top = PLANET_RADIUS + 8;
    }
    s.updateProjectionMatrix();
  }

  private updateCamera(dt: number, speed: number): void {
    if (this.state === 'title' || this.state === 'loading') {
      this.titleSpin += dt * 0.1;
      const r = PLANET_RADIUS * 2.9;
      this.camera.position.set(Math.sin(this.titleSpin) * r, r * 0.42, Math.cos(this.titleSpin) * r);
      this.camera.up.set(0, 1, 0);
      this.camera.lookAt(0, 5, 0);
      return;
    }
    if (this.flyIn > 0) {
      this.flyIn = Math.max(0, this.flyIn - dt / 2.6);
      const e = this.flyIn * this.flyIn * (3 - 2 * this.flyIn);
      this.follow.distance = 7 + e * 26;
      this.follow.pitch = 0.42 + e * 0.8;
    }
    this.follow.update(dt, this.heroPos, this.player, speed > 0.4);
  }

  private updateZones(): void {
    if (this.state !== 'play') return;
    const dir = this.player.position.clone().normalize();
    const weights: Partial<Record<AmbienceKind, number>> = {};
    let inside: string | null = null;
    for (const z of this.zones) {
      const dist = dir.angleTo(z.dir) * PLANET_RADIUS;
      weights[z.ambience] = Math.max(weights[z.ambience] ?? 0, THREE.MathUtils.clamp((z.radius + 6 - dist) / 6, 0, 1));
      if (dist < z.radius && !inside) inside = z.name;
    }
    this.audio.setAmbience(weights);
    if (inside && inside !== this.zone && !this.dialogue && this.flyIn === 0) {
      this.ui.showZone(inside);
      this.audio.pop();
    }
    this.zone = inside;
  }

  private updateMarkers(): void {
    const hud = this.hud;
    hud.beginMarkers();
    if (this.state === 'play') {
      const camDir = this.camera.getWorldDirection(new THREE.Vector3());
      const camUp = this.camera.position.clone().normalize();
      const show = (world: THREE.Vector3, text: string, cls: string, maxDist = 24) => {
        if (world.distanceTo(this.camera.position) > maxDist) return;
        if (world.clone().sub(this.camera.position).dot(camDir) <= 0.1) return;
        // Skip anything on the far side of the planet.
        if (world.clone().normalize().dot(camUp) < 0.7) return;
        const p = world.clone().project(this.camera);
        hud.marker((p.x * 0.5 + 0.5) * innerWidth, (-p.y * 0.5 + 0.5) * innerHeight, text, cls);
      };
      for (const npc of this.npcs) {
        if (this.dialogue?.npc === npc) continue;
        const head = npc.pos.clone().addScaledVector(npc.up, 1.85);
        const q = this.quests.markerFor(npc.spec.id);
        if (npc.bubbleUntil > this.time) show(head.clone().addScaledVector(npc.up, 0.5), npc.bubble, 'bubble shout');
        else if (q) show(head, q, q === '!' ? 'quest' : 'turnin', 40);
        else if (npc.spec.shop) show(head, SHOPS[npc.spec.shop].name, 'shop', 18);
        if (npc.pos.distanceTo(this.player.position) < 7 && npc.bubbleUntil <= this.time) {
          show(npc.pos.clone().addScaledVector(npc.up, q || npc.spec.shop ? 2.35 : 1.75), npc.spec.name, 'name');
        }
      }
      for (const [w, b] of this.walkerBubbles) {
        if (b.until < this.time) { this.walkerBubbles.delete(w); continue; }
        const up = w.char.root.position.clone().normalize();
        show(w.char.root.position.clone().addScaledVector(up, 1.9), b.text, 'bubble');
      }
      if (this.thiefActive) {
        const up = this.thief.char.root.position.clone().normalize();
        show(this.thief.char.root.position.clone().addScaledVector(up, 1.7), '!', 'quest', 40);
      }
      for (const pk of this.pickups) {
        if (!pk.taken && pk.obj.visible) show(pk.base.clone().addScaledVector(pk.up, 0.7), '\u2726', 'sparkle', 18);
      }
    }
    hud.endMarkers();

    const it = this.interaction;
    if (it) {
      const p = it.pos.clone().addScaledVector(it.pos.clone().normalize(), 1.2).project(this.camera);
      this.ui.setPrompt((p.x * 0.5 + 0.5) * innerWidth, (-p.y * 0.5 + 0.5) * innerHeight + 64, true, it.label);
    } else {
      this.ui.setPrompt(0, 0, false);
    }
  }

  private resize(): void {
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, window.devicePixelRatio > 2 ? 1.5 : 1.25);
    const bw = Math.floor(w * dpr), bh = Math.floor(h * dpr);
    if (this.canvas.width === bw && this.canvas.height === bh) return;
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.fov = w < h ? 62 : 50;
    this.camera.updateProjectionMatrix();
    this.ink.setSize();
    this.studio?.resize();
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.hintTimer);
    this.audio.dispose();
    this.ink.dispose();
    this.renderer.dispose();
  }
}
